import { usePrincipals } from '../../hooks/queries';
import { getCurrentPrincipal } from '../../lib/currentPrincipal';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import {
  trackGlobalFilterChange,
  trackPageViewed,
} from '../../utils/principal-service/event-endpoints';
import { Heading } from '../Heading';
import { Tab, Tabs } from '../Tabs';
import { MaintenanceState } from '../MaintenanceState/MaintenanceState';
import {
  getDisabledGlobalFilters,
  GlobalFilters,
  GlobalFilterType,
  showGlobalFilters,
} from '../../features/GlobalFilters';
import { useNavigationContext } from '../../context/NavigationProvider';
import { SecondaryFeature } from '../../utils/types';

export const SecondaryNavigation: React.FC = () => {
  const { data: principals } = usePrincipals();
  const { currentPrincipal } = getCurrentPrincipal(principals);
  const { t } = useTranslation();

  const {
    primaryFeature,
    secondaryFeature,
    setSecondaryFeature,
    tertiaryFeature,
  } = useNavigationContext();

  const [displayGlobalFilters, disabledFilters] = useMemo<
    [boolean, GlobalFilterType[]]
  >(
    () => [
      showGlobalFilters(
        secondaryFeature?.id ?? null,
        tertiaryFeature?.id ?? null,
      ),
      getDisabledGlobalFilters(
        secondaryFeature?.id ?? null,
        tertiaryFeature?.id ?? null,
      ),
    ],
    [secondaryFeature, tertiaryFeature],
  );

  useEffect(() => {
    if (!primaryFeature || !secondaryFeature) {
      return;
    }
    trackPageViewed({
      principalId: currentPrincipal.id,
      content: primaryFeature.id,
      section: secondaryFeature.id satisfies string,
    });
  }, [secondaryFeature, currentPrincipal.id, primaryFeature]);

  return (
    <div className="w-full px-4 md:px-6">
      <div className="max-w-xl-content mx-auto">
        <div
          className={`mb-7 pt-6 md:mb-${
            displayGlobalFilters ? '3' : '9'
          } md:pt-7`}
          data-test="page-header"
        >
          <div className="flex flex-col items-baseline justify-between md:flex-row md:space-x-2">
            <Heading
              text={t(`features:${primaryFeature?.id}:heading`)}
              margin={0}
              level="h1"
            />
            <div className="flex w-full md:w-8/12 md:justify-end">
              <div className="hidden md:block">
                {displayGlobalFilters && (
                  <GlobalFilters
                    disabledFilters={disabledFilters}
                    onSave={(newFilter) => {
                      trackGlobalFilterChange({
                        principalId: currentPrincipal.id,
                        content: 'Insights',
                        section: (secondaryFeature?.id ?? '') satisfies string,
                        filters: {
                          countryCodes:
                            newFilter.countries?.map((c) => c.iso) ?? [],
                          marketSegmentCodes:
                            newFilter.marketSegments
                              ?.map((m) => m.code)
                              .filter((c): c is string => c !== undefined) ??
                            [],
                          portfolioKeys:
                            newFilter.portfolios?.map((p) => p.id) ?? [],
                          territories:
                            newFilter.territories?.map((t) => t.name) ?? [],
                        },
                      });
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <Tabs
          activeTab={(secondaryFeature?.id ?? '') satisfies string}
          onTabChange={(id) => setSecondaryFeature(id as SecondaryFeature)}
        >
          {(primaryFeature?.secondaryFeatures ?? [])
            .sort((a, b) => a.visibilityOrder - b.visibilityOrder)
            .map(({ id }) => (
              <Tab
                key={id}
                tabKey={id}
                title={t(`features:${primaryFeature?.id}:${id}:title`)}
                link={`/${currentPrincipal.slug}/${primaryFeature?.id}/${id}`}
              ></Tab>
            ))}
        </Tabs>
        {secondaryFeature?.maintenanceMode ? <MaintenanceState /> : <Outlet />}
      </div>
    </div>
  );
};
